import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import axiosInstance from '../api/axiosConfig';
import { connectSocket, onSocketReconnect } from './socketClient';
import './AnnouncementAdminDock.css';

/**
 * The admin side of announcement replies: a floating, draggable panel — NOT the
 * locked full-screen popup users get. Minimise / maximise / close and carry on
 * working; replies stream in live over the `role:system_admin` socket room.
 *
 * It renders ONE merged feed of replies across every announcement (newest
 * first), with an optional filter dropdown — so a reply is never hidden just
 * because it landed on an announcement other than the one selected. Closing
 * only hides it; a new reply pops it back (minimised, badged).
 *
 * Mounted once by AppShell for system_admin users only.
 */
const short = (s, n = 46) => (s && s.length > n ? `${s.slice(0, n)}…` : s || '');
const fmtTime = (d) => new Date(d).toLocaleString([], { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });

const AnnouncementAdminDock = () => {
    const { user, token } = useAuth();
    const isAdmin = user?.role === 'system_admin';

    const [list, setList] = useState([]);        // announcements, for the filter dropdown
    const [replies, setReplies] = useState([]);  // merged feed, newest first
    const [filterId, setFilterId] = useState(''); // '' = all announcements
    const [win, setWin] = useState('closed');    // closed | min | normal | max
    const [unseen, setUnseen] = useState(0);
    const [pos, setPos] = useState(null);
    const dragRef = useRef(null);
    const winRef = useRef('closed');
    const listIdsRef = useRef([]);
    winRef.current = win;
    listIdsRef.current = list.map((a) => a.id);

    const loadList = useCallback(async () => {
        try {
            const { data } = await axiosInstance.get('/api/announcements');
            if (data?.success) setList(data.data || []);
        } catch (err) {
            console.error('[announcement-dock] list failed:', err.message);
        }
    }, []);

    const loadReplies = useCallback(async () => {
        try {
            const { data } = await axiosInstance.get('/api/announcements/responses/recent');
            if (data?.success) setReplies(data.data || []);
        } catch (err) {
            console.error('[announcement-dock] replies failed:', err.message);
        }
    }, []);

    // Initial load.
    useEffect(() => {
        if (!isAdmin || !token) return;
        loadList();
        loadReplies();
    }, [isAdmin, token, loadList, loadReplies]);

    // Recovery: a missed 'announcement:new'/'announcement:response' event
    // (admin's tab was disconnected) shouldn't leave the dock's feed stale —
    // reconnect just re-runs the same two loads above.
    useEffect(() => {
        if (!isAdmin || !token) return undefined;
        return onSocketReconnect(() => {
            loadList();
            loadReplies();
        });
    }, [isAdmin, token, loadList, loadReplies]);

    // Live channel.
    useEffect(() => {
        if (!isAdmin || !token) return undefined;
        const socket = connectSocket(token);
        if (!socket) return undefined;

        const onNew = (a) => setList((prev) => [a, ...prev.filter((x) => x.id !== a.id)].slice(0, 20));

        const onResponse = (r) => {
            // Prepend to the merged feed regardless of the current filter — the
            // feed is the source of truth, the filter is only a view.
            setReplies((prev) => (prev.some((x) => x.id === r.id) ? prev : [r, ...prev]));
            // Make sure the reply's announcement is known to the dropdown.
            if (r.announcementId && !listIdsRef.current.includes(r.announcementId)) loadList();
            const openNow = winRef.current === 'normal' || winRef.current === 'max';
            if (!openNow) setUnseen((n) => n + 1);
            if (winRef.current === 'closed') setWin('min');
        };

        socket.on('announcement:new', onNew);
        socket.on('announcement:response', onResponse);
        return () => {
            socket.off('announcement:new', onNew);
            socket.off('announcement:response', onResponse);
        };
    }, [isAdmin, token, loadList]);

    // Let the admin tab jump straight to one thread.
    useEffect(() => {
        const handler = (e) => {
            setFilterId(e.detail?.id || '');
            setWin('normal');
            setUnseen(0);
            loadReplies();
        };
        window.addEventListener('announcement:open-dock', handler);
        return () => window.removeEventListener('announcement:open-dock', handler);
    }, [loadReplies]);

    const open = (state = 'normal') => { setWin(state); setUnseen(0); };

    // --- drag by header (desktop only; on mobile the dock is a bottom sheet) ---
    const onDragStart = (e) => {
        if (window.innerWidth <= 640) return;
        const startX = e.clientX;
        const startY = e.clientY;
        const rect = e.currentTarget.parentElement.getBoundingClientRect();
        dragRef.current = { startX, startY, baseX: rect.left, baseY: rect.top };
        const move = (ev) => {
            if (!dragRef.current) return;
            const { startX: sx, startY: sy, baseX, baseY } = dragRef.current;
            const x = Math.max(0, Math.min(window.innerWidth - 80, baseX + (ev.clientX - sx)));
            const y = Math.max(0, Math.min(window.innerHeight - 40, baseY + (ev.clientY - sy)));
            setPos({ x, y });
        };
        const up = () => {
            dragRef.current = null;
            window.removeEventListener('mousemove', move);
            window.removeEventListener('mouseup', up);
        };
        window.addEventListener('mousemove', move);
        window.addEventListener('mouseup', up);
    };

    if (!isAdmin) return null;
    // No standalone pill — the one corner anchor is the Help widget's
    // "Announcement replies" item. When closed the dock shows nothing; a new
    // reply still pops it back (minimised) via onResponse, and posting an
    // announcement opens it (AnnouncementAdmin dispatches announcement:open-dock).
    if (win === 'closed') return null;

    // A drag position only applies on desktop — on a narrow screen the CSS
    // pins the dock as a full-width bottom sheet.
    const isNarrow = typeof window !== 'undefined' && window.innerWidth <= 640;
    const posStyle = pos && !isNarrow ? { left: pos.x, top: pos.y, right: 'auto', bottom: 'auto' } : undefined;
    const shown = filterId ? replies.filter((r) => r.announcementId === filterId) : replies;

    return (
        <div className={`ann-dock ann-dock--${win}`} style={posStyle}>
            <div className="ann-dock__header" onMouseDown={onDragStart}>
                <span className="ann-dock__title">
                    📢 Announcement replies
                    {unseen > 0 && <span className="ann-dock__badge">{unseen}</span>}
                </span>
                <span className="ann-dock__controls">
                    {win !== 'min' && (
                        <button type="button" title="Minimise" onClick={() => setWin('min')}>—</button>
                    )}
                    {win !== 'max' ? (
                        <button type="button" title="Maximise" onClick={() => open('max')}>▢</button>
                    ) : (
                        <button type="button" title="Restore" onClick={() => open('normal')}>❐</button>
                    )}
                    {win === 'min' && (
                        <button type="button" title="Open" onClick={() => open('normal')}>▲</button>
                    )}
                    <button type="button" title="Close" onClick={() => setWin('closed')}>✕</button>
                </span>
            </div>

            {win !== 'min' && (
                <div className="ann-dock__body">
                    <select
                        className="ann-dock__select"
                        value={filterId}
                        onChange={(e) => setFilterId(e.target.value)}
                    >
                        <option value="">All announcements</option>
                        {list.map((a) => (
                            <option key={a.id} value={a.id}>
                                {short(a.message)} · {fmtTime(a.createdAt)}
                            </option>
                        ))}
                    </select>

                    <div className="ann-dock__list">
                        {shown.length === 0 ? (
                            <div className="ann-dock__empty">No replies yet.</div>
                        ) : (
                            shown.map((r) => (
                                <div key={r.id} className="ann-dock__reply">
                                    <div className="ann-dock__reply-head">
                                        <span className="ann-dock__reply-name">{r.userName}</span>
                                        <span className="ann-dock__reply-time">{fmtTime(r.createdAt)}</span>
                                    </div>
                                    <div className="ann-dock__reply-text">{r.text}</div>
                                    {!filterId && r.announcementMessage && (
                                        <div className="ann-dock__reply-ann" title={r.announcementMessage}>
                                            ↳ {short(r.announcementMessage, 52)}
                                        </div>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default AnnouncementAdminDock;
